import { useEffect, useState, useRef } from 'react';
import { monthsOf, yearsOfMonths } from '../lib/duration';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import type { ReportingProfileView } from '../api/remediation';
import { DataItemsPanel } from '../components/data-items-panel';
import { CryptoInventoryPanel } from '../components/crypto-inventory-panel';
import type {
  Project, Asset, ParameterValueRead,
  LikertLevel, CryptoAgility, Upgradability,
  AssetStatus, AssetEnvironment, AssetExposure,
} from '../api/types';
import {
  LIKERT_OPTIONS, CRYPTO_AGILITY_OPTIONS, UPGRADABILITY_OPTIONS,
  migrationCostOptionsFor, PARAM_IDS,
} from '../lib/form-options';

/**
 * Unified asset form — Doc 3 §6's central UI primitive.
 *
 * One form. Sections render based on the project's module_scope. The
 * principle is "every module assesses every asset," so when a module is
 * in scope, its inputs are visible regardless of asset_type.
 *
 * Save flow:
 *   1. Create or update the asset (POST /assets or PATCH /assets/:id),
 *      writing the asset-column fields (business_criticality, crypto_agility,
 *      upgradability, etc.) plus type_attributes for vendor and data fields.
 *   2. If creating, enroll into the project (POST /projects/:id/assets).
 *   3. PUT the captured parameter values (PUT
 *      /projects/:projectId/assets/:assetId/parameter-values).
 *
 * Three round-trips. Slice 6c can collapse this to one in a service-layer
 * helper.
 *
 * Slice 6b additions:
 *   - URL ?project= fallback for project_id (AssetGrid links use this).
 *   - Save-and-add-another button (resets form state in-place).
 *   - Dirty-state warning on browser navigation away.
 */

interface Catalog {
  id: string;
  code: string;
  name: string;
  family: string;
}

/**
 * Slice 7.8.1 (bugfix): merge form-managed type_attributes onto the
 * original captured at hydrate time, so editing an asset doesn't drop
 * unknown keys (data_state, encryption_at_rest, customer extensions).
 *
 * Empty/falsy form values leave the original key untouched, allowing a
 * user to edit a partial set of fields without zeroing the rest. To
 * remove a key the user must clear it explicitly via a different flow
 * (not yet implemented; tracked in BACKLOG).
 *
 * Exported for unit testing.
 */
export function mergeTypeAttributes(
  original: Record<string, unknown>,
  formFields: {
    vendorSupplied?: string;
    vendorName?: string;
    vendorReadiness?: string;
    valueOfAsset?: string;
    altSourcing?: string;
    activeEngagement?: string;
    procurementTimelines?: string;
    sourceCountryRisk?: string;
    dataSensitivity?: string;
    sndlExposure?: string;
  },
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...original };
  if (formFields.vendorSupplied)        out.vendor_supplied_asset            = formFields.vendorSupplied;
  if (formFields.vendorName?.trim())    out.vendor_name                      = formFields.vendorName.trim();
  if (formFields.vendorReadiness)       out.vendor_readiness                 = formFields.vendorReadiness;
  if (formFields.valueOfAsset)          out.value_of_asset                   = formFields.valueOfAsset;
  if (formFields.altSourcing)           out.alternative_sourcing_difficulty  = formFields.altSourcing;
  if (formFields.activeEngagement)      out.active_engagement                = formFields.activeEngagement;
  if (formFields.procurementTimelines)  out.procurement_timelines_cycles     = formFields.procurementTimelines;
  if (formFields.sourceCountryRisk)     out.source_country_risk              = formFields.sourceCountryRisk;
  if (formFields.dataSensitivity)       out.data_sensitivity                 = formFields.dataSensitivity;
  if (formFields.sndlExposure)          out.sndl_threat_exposure             = formFields.sndlExposure;
  return out;
}

/**
 * Slice 7.8.3 (bugfix): determine which business_unit_id to send when
 * creating or updating an asset.
 *
 * Project-level scopes work differently:
 *   - Business_Unit: project.scope_business_unit_id is set, all assets
 *     in scope belong to that BU. Use it directly.
 *   - Company: project.scope_business_unit_id is NULL. The BU has to
 *     come from the asset itself.
 *   - System: rare case; same as Company — derive from system.
 *
 * Resolution order:
 *   1. The currently-loaded asset's business_unit_id (edit flow always
 *      has this; trustworthy, came from the DB).
 *   2. The selected system's business_unit_id (looked up in the
 *      systems list). Used during create flow when the project is
 *      Company-scoped.
 *   3. project.scope_business_unit_id (works for BU-scoped projects).
 *   4. null — caller treats this as a validation failure (form's
 *      canSubmit will surface it before save).
 *
 * Exported for unit testing. Pure function — no side effects.
 */
export function deriveBusinessUnitId(input: {
  existingAssetBusinessUnitId?: string | null;
  selectedSystemId?: string | null;
  systemsList?: Array<{ id: string; business_unit_id?: string | null }>;
  projectScopeBusinessUnitId?: string | null;
}): string | null {
  if (input.existingAssetBusinessUnitId) {
    return input.existingAssetBusinessUnitId;
  }
  if (input.selectedSystemId && input.systemsList) {
    const sys = input.systemsList.find((s) => s.id === input.selectedSystemId);
    if (sys?.business_unit_id) return sys.business_unit_id;
  }
  if (input.projectScopeBusinessUnitId) {
    return input.projectScopeBusinessUnitId;
  }
  return null;
}

/**
 * Slice 7.9 (item 4): compute the set of fields that differ between
 * `current` and `original` form snapshots. Returns a Set of backend
 * field names (snake_case) that callers can use to assemble a partial
 * PATCH payload.
 *
 * The Set keys match `CreateAssetSchema` field names plus four PV-specific
 * synthetic keys (pv_regulatory_level, pv_migration_cost, pv_complexity,
 * pv_feasibility). type_attributes is treated atomically — any sub-key
 * change marks the whole field dirty (the backend stores it as a JSON
 * column without partial-merge support).
 *
 * Exported for unit testing. Pure function — no side effects.
 */
export interface AssetFormSnapshot {
  name: string;
  description: string;
  external_ref: string;
  asset_type_id: string;
  asset_source_id: string;
  system_id: string;
  business_criticality: string;
  expected_shelf_life: string;
  time_for_migration: string;
  data_security_lifetime: string;
  migration_start_date: string;
  crypto_agility: string;
  upgradability: string;
  legacy_system: boolean;
  hardware_resource_constraints: string;
  certification_requirement: boolean;
  source_code_availability: boolean;
  // Unified platform contract columns (Asset Unification)
  status: string;
  environment: string;
  exposure: string;
  type_attributes: Record<string, unknown>;
  regulatory_level: string;
  migration_cost: string;
  complexity: string;
  feasibility: string;
}

export function computeAssetFormDirtyFields(
  original: AssetFormSnapshot,
  current: AssetFormSnapshot,
): Set<string> {
  const dirty = new Set<string>();
  if (current.name !== original.name) dirty.add('name');
  if (current.description !== original.description) dirty.add('description');
  if (current.external_ref !== original.external_ref) dirty.add('external_ref');
  if (current.asset_type_id !== original.asset_type_id) dirty.add('asset_type_id');
  if (current.asset_source_id !== original.asset_source_id) dirty.add('asset_source_id');
  if (current.system_id !== original.system_id) dirty.add('system_id');
  if (current.business_criticality !== original.business_criticality) dirty.add('business_criticality');
  if (current.expected_shelf_life !== original.expected_shelf_life) dirty.add('expected_shelf_life');
  if (current.time_for_migration !== original.time_for_migration) dirty.add('time_for_migration');
  if (current.data_security_lifetime !== original.data_security_lifetime) dirty.add('data_security_lifetime');
  if (current.migration_start_date !== original.migration_start_date) dirty.add('migration_start_date');
  if (current.crypto_agility !== original.crypto_agility) dirty.add('crypto_agility');
  if (current.upgradability !== original.upgradability) dirty.add('upgradability');
  if (current.legacy_system !== original.legacy_system) dirty.add('legacy_system');
  if (current.hardware_resource_constraints !== original.hardware_resource_constraints) dirty.add('hardware_resource_constraints');
  if (current.certification_requirement !== original.certification_requirement) dirty.add('certification_requirement');
  if (current.source_code_availability !== original.source_code_availability) dirty.add('source_code_availability');
  if (current.status !== original.status) dirty.add('status');
  if (current.environment !== original.environment) dirty.add('environment');
  if (current.exposure !== original.exposure) dirty.add('exposure');
  // type_attributes: any sub-key change marks the whole field dirty.
  const taKeys = new Set([...Object.keys(current.type_attributes), ...Object.keys(original.type_attributes)]);
  for (const k of taKeys) {
    if (current.type_attributes[k] !== original.type_attributes[k]) {
      dirty.add('type_attributes');
      break;
    }
  }
  if (current.regulatory_level !== original.regulatory_level) dirty.add('pv_regulatory_level');
  if (current.migration_cost !== original.migration_cost) dirty.add('pv_migration_cost');
  if (current.complexity !== original.complexity) dirty.add('pv_complexity');
  if (current.feasibility !== original.feasibility) dirty.add('pv_feasibility');
  return dirty;
}

export function AssetForm() {
  const params = useParams<{ projectId?: string; assetId?: string }>();
  const [searchParams] = useSearchParams();
  // Slice 6b: project ID can come from path param OR ?project= search param.
  const projectId = params.projectId ?? searchParams.get('project') ?? null;
  const assetId = params.assetId;
  const isEdit = !!assetId;
  const navigate = useNavigate();
  const qc = useQueryClient();

  // The project currency labels the cost bands.
  const profileQ = useQuery({
    queryKey: ['reporting-profile', projectId],
    queryFn: () => api.get<ReportingProfileView>(`/projects/${projectId}/reporting-profile`),
    enabled: !!projectId,
  });
  const projectQ = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
    enabled: !!projectId,
  });

  // Slice 7.8.3 (bugfix): fetch systems by business_unit_id when the
  // project is BU-scoped, otherwise by company_id when the project is
  // Company-scoped. Previously this query was disabled entirely for
  // Company-scoped projects, leaving the System dropdown empty and
  // making the form unsaveable.
  const systemsQ = useQuery({
    queryKey: [
      'systems',
      projectQ.data?.scope_business_unit_id ?? null,
      projectQ.data?.scope_company_id ?? null,
    ],
    queryFn: () => {
      const buId = projectQ.data?.scope_business_unit_id;
      const coId = projectQ.data?.scope_company_id;
      if (buId) {
        return api.get<Array<{ id: string; name: string; business_unit_id: string }>>(
          `/systems?business_unit_id=${buId}`,
        );
      }
      if (coId) {
        return api.get<Array<{ id: string; name: string; business_unit_id: string }>>(
          `/systems?company_id=${coId}`,
        );
      }
      return Promise.resolve([]);
    },
    enabled: !!(projectQ.data?.scope_business_unit_id || projectQ.data?.scope_company_id),
  });

  const assetTypesQ = useQuery({
    queryKey: ['asset-types'],
    queryFn: () => api.get<Catalog[]>('/asset-types'),
  });

  const assetSourcesQ = useQuery({
    queryKey: ['asset-sources'],
    queryFn: () => api.get<Catalog[]>('/asset-sources'),
  });

  const existingAssetQ = useQuery({
    queryKey: ['asset', assetId],
    queryFn: () => api.get<Asset>(`/assets/${assetId}`),
    enabled: isEdit,
  });

  const existingPVQ = useQuery({
    queryKey: ['parameter-values', projectId, assetId],
    queryFn: () =>
      api.get<ParameterValueRead[]>(
        `/projects/${projectId}/assets/${assetId}/parameter-values`,
      ),
    enabled: isEdit,
  });

  // ---- Form state ----
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // Customer-owned identifier (e.g. CMDB ID) — used by CSV import
  // deduplication (Append/Upsert modes match on external_ref).
  const [externalRef, setExternalRef] = useState('');
  const [assetTypeId, setAssetTypeId] = useState('');
  const [assetSourceId, setAssetSourceId] = useState('');
  const [systemId, setSystemId] = useState('');

  // Asset columns
  const [businessCriticality, setBusinessCriticality] = useState<LikertLevel | ''>('');
  const [expectedShelfLife, setExpectedShelfLife] = useState('');
  const [timeForMigration, setTimeForMigration] = useState('');
  const [dataSecurityLifetime, setDataSecurityLifetime] = useState('');
  const [migrationStartDate, setMigrationStartDate] = useState('');
  const [cryptoAgility, setCryptoAgility] = useState<CryptoAgility | ''>('');
  const [upgradability, setUpgradability] = useState<Upgradability | ''>('');
  const [legacySystem, setLegacySystem] = useState(false);
  const [hardwareConstraints, setHardwareConstraints] = useState<LikertLevel | ''>('');
  const [certificationRequired, setCertificationRequired] = useState(false);
  const [sourceCodeAvailable, setSourceCodeAvailable] = useState(false);

  // Unified platform contract columns (Asset Unification). Empty string
  // means "not set" — create defaults are applied server-side
  // (environment=PRODUCTION; status/exposure derived from their QRAM twins).
  const [status, setStatus] = useState<AssetStatus | ''>('');
  const [environment, setEnvironment] = useState<AssetEnvironment | ''>('');
  const [exposure, setExposure] = useState<AssetExposure | ''>('');

  // Type attributes (vendor + data)
  const [vendorSupplied, setVendorSupplied] = useState<'Yes' | 'No' | ''>('');
  const [vendorName, setVendorName] = useState('');
  const [vendorReadiness, setVendorReadiness] = useState<LikertLevel | ''>('');
  const [valueOfAsset, setValueOfAsset] = useState<LikertLevel | ''>('');
  const [altSourcing, setAltSourcing] = useState<LikertLevel | ''>('');
  const [activeEngagement, setActiveEngagement] = useState<LikertLevel | ''>('');
  const [procurementTimelines, setProcurementTimelines] = useState<LikertLevel | ''>('');
  const [sourceCountryRisk, setSourceCountryRisk] = useState<LikertLevel | ''>('');
  const [dataSensitivity, setDataSensitivity] = useState<LikertLevel | ''>('');
  const [sndlExposure, setSndlExposure] = useState<LikertLevel | ''>('');

  // Captured parameter values
  const [regulatoryLevel, setRegulatoryLevel] = useState<LikertLevel | ''>('');
  const [migrationCost, setMigrationCost] = useState('');
  const [complexity, setComplexity] = useState<LikertLevel | ''>('');
  const [feasibility, setFeasibility] = useState<LikertLevel | ''>('');

  // ============================================================
  // Slice 7.9 (item 4): per-field dirty tracking.
  //
  // Replaces the prior single dirtyRef-bool. On hydration, we capture
  // a snapshot of every field as `originalFormRef.current`. To compute
  // dirty fields on save (or for the unsaved-changes warning), we
  // diff current state against the snapshot via the exported
  // `computeAssetFormDirtyFields` helper.
  //
  // Why bother:
  // - The v0.7.3 BU regression came from sending wholesale payloads
  //   that re-validated unchanged fields. With per-field tracking, an
  //   edit-flow PATCH sends only what actually changed; unchanged
  //   fields can't fail validation.
  // - Hydration sets state via setX; without the snapshot pattern, every
  //   hydrate would mark every field dirty. The snapshot captures the
  //   post-hydration baseline so only post-hydration user edits count.
  // ============================================================
  const originalFormRef = useRef<AssetFormSnapshot | null>(null);

  // Build a fresh AssetFormSnapshot from current state. Used to (a) seed
  // the original ref on hydration, and (b) diff against original on save
  // to determine dirty fields.
  function captureSnapshot(): AssetFormSnapshot {
    return {
      name,
      description,
      external_ref: externalRef,
      asset_type_id: assetTypeId,
      asset_source_id: assetSourceId,
      system_id: systemId,
      business_criticality: businessCriticality,
      expected_shelf_life: expectedShelfLife,
      time_for_migration: timeForMigration,
      data_security_lifetime: dataSecurityLifetime,
      migration_start_date: migrationStartDate,
      crypto_agility: cryptoAgility,
      upgradability,
      legacy_system: legacySystem,
      hardware_resource_constraints: hardwareConstraints,
      certification_requirement: certificationRequired,
      source_code_availability: sourceCodeAvailable,
      status,
      environment,
      exposure,
      type_attributes: {
        vendor_supplied_asset: vendorSupplied,
        vendor_name: vendorName,
        vendor_readiness: vendorReadiness,
        value_of_asset: valueOfAsset,
        alternative_sourcing_difficulty: altSourcing,
        active_engagement: activeEngagement,
        procurement_timelines_cycles: procurementTimelines,
        source_country_risk: sourceCountryRisk,
        data_sensitivity: dataSensitivity,
        sndl_threat_exposure: sndlExposure,
      },
      regulatory_level: regulatoryLevel,
      migration_cost: migrationCost,
      complexity,
      feasibility,
    };
  }

  // Slice 7.8.1 (bugfix): preserve type_attributes fields the form
  // doesn't know about. The form has explicit state for vendor_*,
  // data_sensitivity, sndl_threat_exposure, value_of_asset etc., but
  // an asset's type_attributes JSON may carry additional keys (e.g.
  // data_state, encryption_at_rest, customer-extension fields). Save
  // must merge form-managed fields onto a copy of the original instead
  // of replacing — otherwise editing a data asset wipes its data_state
  // and encryption_at_rest, which 7.8.1 just added.
  const originalTypeAttrsRef = useRef<Record<string, unknown>>({});

  // Mark dirty whenever any tracked state changes after initial hydration.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!hydratedRef.current) {
      // Mark hydrated on the first render after data load (or immediately
      // for create flow where there's no existing data).
      if (!isEdit || existingAssetQ.data) {
        hydratedRef.current = true;
        // Slice 7.9: capture the post-hydration baseline. After this point
        // any state change is a user edit and computeDirtyFields will
        // detect it.
        originalFormRef.current = captureSnapshot();
      }
    }
    // The dependency array is intentionally exhaustive over all editable
    // fields so React re-runs this effect on any change. We don't actually
    // do anything in the body except capture the baseline once — the dirty
    // calculation happens lazily in computeDirtyFields below.
  }, [
    name, description, externalRef, businessCriticality, expectedShelfLife, timeForMigration, dataSecurityLifetime, migrationStartDate,
    cryptoAgility, upgradability, legacySystem, hardwareConstraints,
    certificationRequired, sourceCodeAvailable, status, environment, exposure,
    vendorSupplied, vendorName, vendorReadiness,
    valueOfAsset, altSourcing, activeEngagement, procurementTimelines,
    sourceCountryRisk, dataSensitivity, sndlExposure, regulatoryLevel,
    migrationCost, complexity, feasibility, isEdit, existingAssetQ.data,
  ]);

  // Compute the set of currently-dirty fields by diffing current state
  // against the captured snapshot. Returns an empty set if the snapshot
  // hasn't been taken yet (still hydrating).
  function computeDirtyFields(): Set<string> {
    if (!originalFormRef.current) return new Set();
    return computeAssetFormDirtyFields(originalFormRef.current, captureSnapshot());
  }

  // Boolean version for the unsaved-changes warning (preserves prior
  // behavior of dirtyRef).
  function isDirty(): boolean {
    return computeDirtyFields().size > 0;
  }

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset all form fields to defaults — used by "Save and add another".
  const resetForm = () => {
    setName('');
    setDescription('');
    // external_ref is a per-asset identifier — carrying it into the next
    // "add another" row would silently create duplicate refs.
    setExternalRef('');
    // Keep asset type, source, and system as-is — when adding a batch
    // of assets to the same system, the user typically wants the next
    // row pre-filled with the same context.
    setBusinessCriticality('');
    setExpectedShelfLife('');
    setTimeForMigration('');
    setDataSecurityLifetime('');
    setMigrationStartDate('');
    setCryptoAgility('');
    setUpgradability('');
    setLegacySystem(false);
    setHardwareConstraints('');
    setCertificationRequired(false);
    setSourceCodeAvailable(false);
    setStatus('');
    setEnvironment('');
    setExposure('');
    setVendorSupplied('');
    setVendorName('');
    setVendorReadiness('');
    setValueOfAsset('');
    setAltSourcing('');
    setActiveEngagement('');
    setProcurementTimelines('');
    setSourceCountryRisk('');
    setDataSensitivity('');
    setSndlExposure('');
    setRegulatoryLevel('');
    setMigrationCost('');
    setComplexity('');
    setFeasibility('');
    // Slice 7.9: re-baseline after reset so the cleared form starts
    // clean. Subsequent edits will diff against this fresh baseline.
    // We have to defer to the next tick because the setX calls above
    // queue state updates; capturing now would still see stale state.
    setTimeout(() => {
      originalFormRef.current = captureSnapshot();
    }, 0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Hydrate form from existing asset on edit
  useEffect(() => {
    const a = existingAssetQ.data;
    if (!a) return;
    setName(a.name);
    setDescription(a.description ?? '');
    setExternalRef(a.external_ref ?? '');
    setAssetTypeId(a.asset_type_id);
    setAssetSourceId(a.asset_source_id);
    setSystemId(a.system_id);
    setBusinessCriticality(a.business_criticality ?? '');
    setExpectedShelfLife(a.expected_shelf_life?.toString() ?? '');
    setTimeForMigration(a.time_for_migration == null || String(a.time_for_migration) === '' ? '' : String(monthsOf(Number(a.time_for_migration))));
    setDataSecurityLifetime(a.data_security_lifetime?.toString() ?? '');
    setMigrationStartDate(a.migration_start_date ? String(a.migration_start_date).slice(0, 10) : '');
    setCryptoAgility(a.crypto_agility ?? '');
    setUpgradability(a.upgradability ?? '');
    setLegacySystem(a.legacy_system ?? false);
    setHardwareConstraints(a.hardware_resource_constraints ?? '');
    setCertificationRequired(a.certification_requirement ?? false);
    setSourceCodeAvailable(a.source_code_availability ?? false);
    setStatus(a.status ?? '');
    setEnvironment(a.environment ?? '');
    setExposure(a.exposure ?? '');
    const ta = a.type_attributes ?? {};
    // Slice 7.8.1: stash a copy of every key for save-time merge.
    // Avoids dropping fields the form doesn't surface (e.g. data_state,
    // encryption_at_rest, customer-extension keys).
    originalTypeAttrsRef.current = { ...ta };
    setVendorSupplied((ta.vendor_supplied_asset as 'Yes' | 'No' | undefined) ?? '');
    setVendorName(typeof ta.vendor_name === 'string' ? ta.vendor_name : '');
    setVendorReadiness((ta.vendor_readiness as LikertLevel | undefined) ?? '');
    setValueOfAsset((ta.value_of_asset as LikertLevel | undefined) ?? '');
    setAltSourcing((ta.alternative_sourcing_difficulty as LikertLevel | undefined) ?? '');
    setActiveEngagement((ta.active_engagement as LikertLevel | undefined) ?? '');
    setProcurementTimelines((ta.procurement_timelines_cycles as LikertLevel | undefined) ?? '');
    setSourceCountryRisk((ta.source_country_risk as LikertLevel | undefined) ?? '');
    setDataSensitivity((ta.data_sensitivity as LikertLevel | undefined) ?? '');
    setSndlExposure((ta.sndl_threat_exposure as LikertLevel | undefined) ?? '');
  }, [existingAssetQ.data]);

  // Hydrate captured values from existing parameter_values on edit
  useEffect(() => {
    const pvs = existingPVQ.data;
    if (!pvs) return;
    for (const pv of pvs) {
      if (pv.parameter_id === PARAM_IDS.regulatory_requirements_level) {
        setRegulatoryLevel((pv.value_text as LikertLevel | null) ?? '');
      } else if (pv.parameter_id === PARAM_IDS.migration_cost_range) {
        setMigrationCost(pv.value_text ?? '');
      } else if (pv.parameter_id === PARAM_IDS.complexity) {
        setComplexity((pv.value_text as LikertLevel | null) ?? '');
      } else if (pv.parameter_id === PARAM_IDS.feasibility) {
        setFeasibility((pv.value_text as LikertLevel | null) ?? '');
      }
    }
  }, [existingPVQ.data]);

  // Default to first system if creating new (edit hydrates from existing asset)
  useEffect(() => {
    if (!isEdit && !systemId && systemsQ.data && systemsQ.data.length > 0) {
      setSystemId(systemsQ.data[0]!.id);
    }
  }, [isEdit, systemId, systemsQ.data]);

  const save = useMutation({
    mutationFn: async () => {
      if (!projectQ.data) throw new Error('Project not loaded');
      const project = projectQ.data;

      // Slice 7.8.1 (bugfix): merge form-managed values onto the
      // captured original so unknown keys (data_state, encryption_at_rest,
      // customer extensions) survive the round-trip.
      const typeAttributes = mergeTypeAttributes(originalTypeAttrsRef.current, {
        vendorSupplied,
        vendorName,
        vendorReadiness,
        valueOfAsset,
        altSourcing,
        activeEngagement,
        procurementTimelines,
        sourceCountryRisk,
        dataSensitivity,
        sndlExposure,
      });

      // Slice 7.8.3 (bugfix): derive business_unit_id from the most
      // reliable source. Company-scoped projects have null
      // scope_business_unit_id; under the previous code that null
      // went straight to the backend payload and triggered a
      // validation error.
      const businessUnitId = deriveBusinessUnitId({
        existingAssetBusinessUnitId: existingAssetQ.data?.business_unit_id ?? null,
        selectedSystemId: systemId,
        systemsList: systemsQ.data,
        projectScopeBusinessUnitId: project.scope_business_unit_id,
      });

      // Slice 7.9 (item 4): on EDIT, send only fields the user actually
      // changed. On CREATE, the backend's CreateAssetSchema requires the
      // full set, so we send everything.
      //
      // The dirty set is computed by diffing current form state against
      // the snapshot captured on hydration. Fields the user never
      // touched aren't sent — the backend treats omitted fields in the
      // PATCH partial as "no change," so unchanged fields can't trigger
      // validation errors. This is the structural fix for the v0.7.3
      // BU regression class.
      const dirty = computeDirtyFields();

      let payload: Record<string, unknown>;
      if (isEdit) {
        // Partial PATCH: only fields the user changed.
        payload = {};
        if (dirty.has('name')) payload.name = name;
        if (dirty.has('description')) payload.description = description || null;
        if (dirty.has('external_ref')) payload.external_ref = externalRef.trim() || null;
        if (dirty.has('asset_type_id')) payload.asset_type_id = assetTypeId;
        if (dirty.has('asset_source_id')) payload.asset_source_id = assetSourceId;
        if (dirty.has('business_criticality')) payload.business_criticality = businessCriticality || null;
        if (dirty.has('expected_shelf_life')) payload.expected_shelf_life = expectedShelfLife === '' ? null : Number(expectedShelfLife);
        if (dirty.has('time_for_migration')) payload.time_for_migration = timeForMigration === '' ? null : yearsOfMonths(Number(timeForMigration));
        if (dirty.has('data_security_lifetime')) payload.data_security_lifetime = dataSecurityLifetime === '' ? null : Number(dataSecurityLifetime);
        if (dirty.has('migration_start_date')) payload.migration_start_date = migrationStartDate || null;
        if (dirty.has('crypto_agility')) payload.crypto_agility = cryptoAgility || null;
        if (dirty.has('upgradability')) payload.upgradability = upgradability || null;
        if (dirty.has('legacy_system')) payload.legacy_system = legacySystem;
        if (dirty.has('hardware_resource_constraints')) payload.hardware_resource_constraints = hardwareConstraints || null;
        if (dirty.has('certification_requirement')) payload.certification_requirement = certificationRequired;
        if (dirty.has('source_code_availability')) payload.source_code_availability = sourceCodeAvailable;
        // Unified platform contract columns — only send when actually set;
        // the backend schema does not accept empty strings.
        if (dirty.has('status') && status) payload.status = status;
        if (dirty.has('environment') && environment) payload.environment = environment;
        if (dirty.has('exposure') && exposure) payload.exposure = exposure;
        if (dirty.has('type_attributes')) payload.type_attributes = typeAttributes;
        // System changes need the system_id AND a recomputed BU because
        // BU is denormalized from the system's BU on the backend. Only
        // include when system_id is actually dirty.
        if (dirty.has('system_id')) {
          if (!businessUnitId) {
            throw new Error(
              'Cannot determine business unit for the selected system. Pick a System whose BU we can resolve.',
            );
          }
          payload.system_id = systemId;
          payload.business_unit_id = businessUnitId;
        }
      } else {
        // Create flow: full payload (backend's CreateAssetSchema requires it).
        if (!businessUnitId) {
          throw new Error(
            'Cannot determine business unit. Pick a System whose BU we can resolve, or contact support if this persists.',
          );
        }
        payload = {
          company_id: project.scope_company_id,
          business_unit_id: businessUnitId,
          system_id: systemId,
          asset_type_id: assetTypeId,
          asset_source_id: assetSourceId,
          name,
          description: description || null,
          external_ref: externalRef.trim() || null,
          business_criticality: businessCriticality || null,
          expected_shelf_life: expectedShelfLife === '' ? null : Number(expectedShelfLife),
          time_for_migration: timeForMigration === '' ? null : yearsOfMonths(Number(timeForMigration)),
          data_security_lifetime: dataSecurityLifetime === '' ? null : Number(dataSecurityLifetime),
          migration_start_date: migrationStartDate || null,
          crypto_agility: cryptoAgility || null,
          upgradability: upgradability || null,
          legacy_system: legacySystem,
          hardware_resource_constraints: hardwareConstraints || null,
          certification_requirement: certificationRequired,
          source_code_availability: sourceCodeAvailable,
          // Unified platform contract columns — omitted fields take server
          // defaults / derivations (environment=PRODUCTION; status/exposure
          // derived from lifecycle_status/external_exposure).
          ...(status ? { status } : {}),
          ...(environment ? { environment } : {}),
          ...(exposure ? { exposure } : {}),
          type_attributes: typeAttributes,
        };
      }

      // Step 1: save the asset. Skip the PATCH entirely if nothing
      // changed (avoids round-tripping for a no-op).
      let savedAssetId = assetId;
      if (isEdit) {
        if (Object.keys(payload).length > 0) {
          await api.patch(`/assets/${assetId}`, payload);
        }
      } else {
        const created = await api.post<Asset>('/assets', payload);
        savedAssetId = created.id;
        // Step 2: enroll the new asset into the project
        await api.post(`/projects/${projectId}/assets`, {
          asset_ids: [savedAssetId],
        });
      }

      // Step 3: write captured parameter values
      const pvWrites: Array<Record<string, unknown>> = [];
      if (regulatoryLevel) {
        pvWrites.push({
          data_type: 'Categorical',
          parameter_id: PARAM_IDS.regulatory_requirements_level,
          value_text: regulatoryLevel,
        });
      }
      if (migrationCost) {
        pvWrites.push({
          data_type: 'Categorical',
          parameter_id: PARAM_IDS.migration_cost_range,
          value_text: migrationCost,
        });
      }
      if (complexity) {
        pvWrites.push({
          data_type: 'Categorical',
          parameter_id: PARAM_IDS.complexity,
          value_text: complexity,
        });
      }
      if (feasibility) {
        pvWrites.push({
          data_type: 'Categorical',
          parameter_id: PARAM_IDS.feasibility,
          value_text: feasibility,
        });
      }
      if (pvWrites.length > 0) {
        await api.put(
          `/projects/${projectId}/assets/${savedAssetId}/parameter-values`,
          { values: pvWrites },
        );
      }

      return savedAssetId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-assets', projectId] });
      qc.invalidateQueries({ queryKey: ['projects', projectId] });
      // Slice 7.9: rebaseline after a successful save. The PATCH succeeded,
      // so current form state IS the new "original". Subsequent edits
      // will be diffed against this fresh baseline — matters for the
      // "save and add another" flow and for the unsaved-changes warning
      // not to fire when the user navigates away after saving.
      originalFormRef.current = captureSnapshot();
      if (saveAndAddAnotherRef.current) {
        saveAndAddAnotherRef.current = false;
        resetForm();
      } else {
        navigate(`/projects/${projectId}`);
      }
    },
  });

  // Used to communicate "save and add another" intent into the mutation's
  // onSuccess (which doesn't have access to the click handler's variable).
  const saveAndAddAnotherRef = useRef(false);

  if (projectQ.isLoading) return <p className="text-ink-500">Loading…</p>;
  if (!projectQ.data) return <p className="text-accent">Project not found.</p>;

  const project = projectQ.data;
  const inScope = (m: string) => project.module_scope[m] === true;
  const canSubmit =
    name.trim() !== '' && assetTypeId !== '' && assetSourceId !== '' && systemId !== '';

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <div className="mb-2">
        <Link
          to={`/projects/${projectId}`}
          className="text-sm text-ink-500 hover:text-ink-900"
        >
          ← Back to project
        </Link>
      </div>

      <h1 className="text-3xl mb-6">
        {isEdit ? 'Edit asset' : 'Add asset'}
      </h1>

      <FormSection title="Identity">
        <div className="space-y-4">
          <div>
            <label htmlFor="name">Name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full"
            />
          </div>
          <div>
            <label htmlFor="desc">Description</label>
            <textarea
              id="desc"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full"
            />
          </div>
          <div>
            <label htmlFor="extref">External ref</label>
            <input
              id="extref"
              type="text"
              value={externalRef}
              onChange={(e) => setExternalRef(e.target.value)}
              maxLength={255}
              className="w-full"
            />
            <p className="text-xs text-ink-500 mt-1">
              Optional — your own ID (e.g. CMDB asset ID). Used to match this asset during CSV imports (Append/Upsert deduplication).
            </p>
          </div>
          <div>
            <label htmlFor="sys">System</label>
            <select
              id="sys"
              value={systemId}
              onChange={(e) => setSystemId(e.target.value)}
              className="w-full"
            >
              <option value="">Select system…</option>
              {(systemsQ.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="atype">Type</label>
              <select
                id="atype"
                value={assetTypeId}
                onChange={(e) => setAssetTypeId(e.target.value)}
                className="w-full"
              >
                <option value="">Select type…</option>
                {(assetTypesQ.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} {t.family && <>· {t.family}</>}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="asource">Source</label>
              <select
                id="asource"
                value={assetSourceId}
                onChange={(e) => setAssetSourceId(e.target.value)}
                className="w-full"
              >
                <option value="">Select source…</option>
                {(assetSourcesQ.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <LikertField
            id="bc"
            label="Business criticality"
            value={businessCriticality}
            onChange={setBusinessCriticality}
          />
          {/* Unified platform contract columns (Asset Unification) */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="ustatus">Status</label>
              <select
                id="ustatus"
                value={status}
                onChange={(e) => setStatus(e.target.value as AssetStatus | '')}
                className="w-full"
              >
                <option value="">Default (from lifecycle)…</option>
                {(['PLANNING', 'DEVELOPMENT', 'TESTING', 'ACTIVE', 'INACTIVE', 'MAINTENANCE', 'DEPRECATED', 'RETIRED'] as const).map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="uenv">Environment</label>
              <select
                id="uenv"
                value={environment}
                onChange={(e) => setEnvironment(e.target.value as AssetEnvironment | '')}
                className="w-full"
              >
                <option value="">Default (PRODUCTION)…</option>
                {(['PRODUCTION', 'DEVELOPMENT', 'TESTING', 'UAT', 'DR', 'SANDBOX'] as const).map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="uexp">Exposure</label>
              <select
                id="uexp"
                value={exposure}
                onChange={(e) => setExposure(e.target.value as AssetExposure | '')}
                className="w-full"
              >
                <option value="">Default (from external exposure)…</option>
                {(['INTERNET', 'EXTERNAL', 'PARTNER', 'INTERNAL', 'PRIVATE', 'ISOLATED'] as const).map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </FormSection>

      {inScope('timeline') && (
        <FormSection title="Timeline">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="esl">Expected shelf life (years)</label>
              <input
                id="esl"
                type="number"
                min="0"
                max="200"
                value={expectedShelfLife}
                onChange={(e) => setExpectedShelfLife(e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label htmlFor="tfm">Time for migration (months)</label>
              <input
                id="tfm"
                type="number"
                min="0"
                max="1200"
                step="1"
                value={timeForMigration}
                onChange={(e) => setTimeForMigration(e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label htmlFor="dsl">Data must stay protected (years)</label>
              <input
                id="dsl"
                type="number"
                min="0"
                max="100"
                value={dataSecurityLifetime}
                onChange={(e) => setDataSecurityLifetime(e.target.value)}
                className="w-full"
                aria-describedby="dsl-hint"
              />
              <p id="dsl-hint" className="text-[11px] text-ink-500 mt-1">How long the data, or trust in its signatures, must stay secure. Empty uses the shelf life.</p>
            </div>
            <div>
              <label htmlFor="msd">Migration start date</label>
              <input
                id="msd"
                type="date"
                value={migrationStartDate}
                onChange={(e) => setMigrationStartDate(e.target.value)}
                className="w-full"
                aria-describedby="msd-hint"
              />
              <p id="msd-hint" className="text-[11px] text-ink-500 mt-1">When work on this asset starts (or started). Empty uses the programme start date.</p>
            </div>
          </div>
          <div className="mt-4">
            <label htmlFor="ca">Crypto agility</label>
            <select
              id="ca"
              value={cryptoAgility}
              onChange={(e) => setCryptoAgility(e.target.value as CryptoAgility | '')}
              className="w-full"
            >
              <option value="">—</option>
              {CRYPTO_AGILITY_OPTIONS.map((o) => (
                <option key={o.code} value={o.code}>{o.label}</option>
              ))}
            </select>
          </div>
        </FormSection>
      )}

      {inScope('data_risk') && (
        <FormSection title="Data Risk">
          <div className="space-y-4">
            <LikertField
              id="ds"
              label="Data sensitivity"
              value={dataSensitivity}
              onChange={setDataSensitivity}
            />
            <LikertField
              id="sndl"
              label="Harvest-now-decrypt-later (HNDL) exposure"
              value={sndlExposure}
              onChange={setSndlExposure}
              hint="Chance the data is being recorded now to be decrypted later by a cryptographically relevant quantum computer (CRQC)."
            />
            <LikertField
              id="reg"
              label="Regulatory & compliance requirements"
              value={regulatoryLevel}
              onChange={setRegulatoryLevel}
            />
          </div>
        </FormSection>
      )}

      {inScope('supply_chain') && (
        <FormSection title="Supply Chain">
          <div className="space-y-4">
            <p className="text-xs text-ink-500" data-testid="asset-vendor-help">
              These fields score this asset&apos;s Supply chain dimension. The vendor&apos;s post-quantum
              commitment (used by the time tests) is set once per vendor in Settings → Reporting profile →
              Vendor commitments; a supplier questionnaire, if the project uses it, is on the Vendor
              readiness tab and can fill in Vendor quantum readiness here.
            </p>
            <div>
              <label htmlFor="vsa">Vendor-supplied asset?</label>
              <select
                id="vsa"
                value={vendorSupplied}
                onChange={(e) => setVendorSupplied(e.target.value as 'Yes' | 'No' | '')}
                className="w-full"
              >
                <option value="">—</option>
                <option value="Yes">Yes</option>
                <option value="No">No (Supply Chain returns N/A)</option>
              </select>
            </div>
            {vendorSupplied === 'Yes' && (
              <div className="space-y-4 border-l-2 border-ink-100 pl-4">
                <div>
                  <label htmlFor="vname">Vendor name</label>
                  <input
                    id="vname"
                    type="text"
                    maxLength={255}
                    value={vendorName}
                    onChange={(e) => setVendorName(e.target.value)}
                    className="w-full"
                    placeholder="e.g. Oracle"
                  />
                  <p className="text-xs text-ink-500 mt-1">
                    Matched to the vendor commitments in Settings → Reporting profile (case and spacing ignored).
                  </p>
                </div>
                <LikertField
                  id="vr"
                  label="Vendor quantum readiness"
                  value={vendorReadiness}
                  onChange={setVendorReadiness}
                  hint="High readiness lowers supply-chain risk (this input is inverted in scoring). A confirmed Vendor readiness questionnaire fed to this asset sets it."
                />
                <LikertField
                  id="voa"
                  label="Value of asset"
                  value={valueOfAsset}
                  onChange={setValueOfAsset}
                />
                <LikertField
                  id="asd"
                  label="Alternative sourcing difficulty"
                  value={altSourcing}
                  onChange={setAltSourcing}
                />
                <LikertField
                  id="ae"
                  label="Active vendor engagement"
                  value={activeEngagement}
                  onChange={setActiveEngagement}
                  hint="High engagement lowers risk (inverted)."
                />
                <LikertField
                  id="pt"
                  label="Procurement timelines & cycles"
                  value={procurementTimelines}
                  onChange={setProcurementTimelines}
                />
                <LikertField
                  id="scr"
                  label="Source country risk"
                  value={sourceCountryRisk}
                  onChange={setSourceCountryRisk}
                />
              </div>
            )}
          </div>
        </FormSection>
      )}

      {inScope('migration_feasibility') && (
        <FormSection title="Migration Feasibility">
          <div className="space-y-4">
            <div>
              <label htmlFor="mc">Migration cost range</label>
              <select
                id="mc"
                value={migrationCost}
                onChange={(e) => setMigrationCost(e.target.value)}
                className="w-full"
              >
                <option value="">—</option>
                {migrationCostOptionsFor(migrationCost, profileQ.data?.currency ?? null).map((o) => (
                  <option key={o.code} value={o.code}>{o.label}</option>
                ))}
              </select>
            </div>
            <LikertField
              id="cmplx"
              label="Complexity"
              value={complexity}
              onChange={setComplexity}
            />
            <LikertField
              id="feas"
              label="Technical & operational feasibility"
              value={feasibility}
              onChange={setFeasibility}
              hint="High feasibility lowers risk (inverted)."
            />
            <div>
              <label htmlFor="upg">Upgradability</label>
              <select
                id="upg"
                value={upgradability}
                onChange={(e) => setUpgradability(e.target.value as Upgradability | '')}
                className="w-full"
              >
                <option value="">—</option>
                {UPGRADABILITY_OPTIONS.map((o) => (
                  <option key={o.code} value={o.code}>{o.label}</option>
                ))}
              </select>
            </div>
            <LikertField
              id="hrc"
              label="Hardware & resource constraints"
              value={hardwareConstraints}
              onChange={setHardwareConstraints}
            />
            <CheckboxField
              id="ls"
              label="Legacy (non-modern) system"
              checked={legacySystem}
              onChange={setLegacySystem}
            />
            <CheckboxField
              id="cr"
              label="Certification recertification required for migration"
              checked={certificationRequired}
              onChange={setCertificationRequired}
            />
            <CheckboxField
              id="sca"
              label="Source code available"
              checked={sourceCodeAvailable}
              onChange={setSourceCodeAvailable}
            />
          </div>
        </FormSection>
      )}

      {/* Slice 7.8: Data Items panel — visible on every asset (Option α
          from the design discussion). Lets users link any asset as a
          data child for Data Risk aggregation. Only shown on edit
          (creates first save the asset, then return to edit and add
          children). */}
      {isEdit && assetId && projectQ.data?.scope_company_id && (
        <DataItemsPanel
          assetId={assetId}
          companyId={projectQ.data.scope_company_id}
        />
      )}

      {/* Slice 9.3 (v0.9.3): asset-centric crypto inventory.
          Shows all keys / certificates / protocols / cipher suites /
          libraries / key stores / signatures associated with this
          asset in one place. The project-level Cryptography tab
          inverts this view (groups by type), which is awkward when
          you want to see "what crypto does this one asset use?". */}
      {isEdit && assetId && projectId && (
        <CryptoInventoryPanel projectId={projectId} assetId={assetId} />
      )}

      <div className="flex gap-3 mt-8 items-center">
        <button
          type="button"
          onClick={() => { saveAndAddAnotherRef.current = false; save.mutate(); }}
          disabled={!canSubmit || save.isPending}
          className="btn-primary"
        >
          {save.isPending && !saveAndAddAnotherRef.current
            ? 'Saving…'
            : isEdit ? 'Save changes' : 'Create asset'}
        </button>
        {!isEdit && (
          <button
            type="button"
            onClick={() => { saveAndAddAnotherRef.current = true; save.mutate(); }}
            disabled={!canSubmit || save.isPending}
            className="btn-secondary"
          >
            {save.isPending && saveAndAddAnotherRef.current
              ? 'Saving…'
              : 'Save and add another'}
          </button>
        )}
        <Link
          to={`/projects/${projectId}`}
          className="btn-secondary no-underline"
          onClick={(e) => {
            if (isDirty() && !confirm('You have unsaved changes. Discard them?')) {
              e.preventDefault();
            }
          }}
        >
          Cancel
        </Link>
        {save.error && (() => {
          // Slice 7.8.1 (bugfix): when the backend returns a Zod
          // validation failure, the body carries an `error.details.issues[]`
          // array with `path` + `message` per failed field. Surfacing
          // those helps the user (or developer) see WHICH field is
          // failing instead of a generic "Request validation failed."
          const e = save.error;
          let issues: Array<{ path: string; message: string }> = [];
          if (e instanceof ApiError) {
            const body = e.body as {
              error?: { details?: { issues?: Array<{ path: string; message: string }> } };
            } | null;
            issues = body?.error?.details?.issues ?? [];
          }
          return (
            <div className="text-accent text-sm self-center">
              <div>{(e as Error).message}</div>
              {issues.length > 0 && (
                <ul className="mt-1 text-xs list-disc list-inside text-ink-700">
                  {issues.map((issue, i) => (
                    <li key={i}>
                      <code className="text-[11px]">{issue.path || '(root)'}</code>: {issue.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function FormSection({ title, children }: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-6 pb-6 border-b border-ink-200 last:border-0">
      <h2 className="text-xl mb-3">{title}</h2>
      {children}
    </section>
  );
}

function LikertField<T extends string>({
  id, label, value, onChange, hint,
}: {
  id: string;
  label: string;
  value: T | '';
  onChange: (v: T | '') => void;
  hint?: string;
}) {
  return (
    <div>
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value as T | '')}
        className="w-full"
      >
        <option value="">—</option>
        {LIKERT_OPTIONS.map((o) => (
          <option key={o.code} value={o.code}>{o.label}</option>
        ))}
      </select>
      {hint && <p className="text-xs text-ink-500 mt-1">{hint}</p>}
    </div>
  );
}

function CheckboxField({
  id, label, checked, onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4"
      />
      <label htmlFor={id} className="mb-0">{label}</label>
    </div>
  );
}